export type APIPlanDefinitionStatus = "archived" | "draft" | "published";

export type PlanRole = "owner" | "supervisor" | "member" | "viewer";

export type WorkspaceScopeMode =
  | "single_workspace"
  | "limited_multi_workspace"
  | "unlimited_multi_workspace";

export type WorkspaceScopeValue =
  | "all"
  | "default_workspace"
  | "explicit_workspace";

export type VendorWorkspaceScope =
  | "explicit_workspace_assignment"
  | "all_workspaces";

export type APIPlanPermissionProfilePresetType = {
  preset_code: string;
  auto_install?: boolean;
};

export type APIPlanApprovalLevelPresetType = {
  level: number;
  label: string;
  description: string;
};

export type APIPlanWorkspaceScopeRulesType = {
  mode: WorkspaceScopeMode;
  max_workspaces: number;
  default_workspace_name: string;
  allow_multi_workspace_users: boolean;
  allow_master_visibility: boolean;
  workspace_selector_visible: boolean;
};

export type APIPlanVendorAccessRulesType = {
  vendors_enabled: boolean;
  allow_vendor_users: boolean;
  allow_vendor_admins: boolean;
  allow_workspace_vendor_creation: boolean;
  vendor_workspace_scope: VendorWorkspaceScope;
};

export type APIPlanDefaultAssignmentType = {
  role: PlanRole;
  permission_profile_preset_code: string;
  approval_level?: number | null;
  workspace_scope: WorkspaceScopeValue;
};

export type APIPlanAccessPresetConfigType = Partial<{
  permission_profile_presets: APIPlanPermissionProfilePresetType[];
  approval_level_presets: APIPlanApprovalLevelPresetType[];
  workspace_scope_rules: APIPlanWorkspaceScopeRulesType;
  vendor_access_rules: APIPlanVendorAccessRulesType;
  default_assignments: Record<string, APIPlanDefaultAssignmentType>;
}>;

export type APIPlanDefinitionConfigType = Partial<{
  access_presets: APIPlanAccessPresetConfigType;
  limits: Record<string, number | string | boolean | null>;
  modules: string[];
  feature_flags: Record<string, boolean | number | string | null>;
}>;

export type APIPlanDefinitionResponseType = Partial<{
  id: number;
  url: string;
  plan_code: string;
  name: string;
  description: string;
  status: APIPlanDefinitionStatus;
  current_version: number;
  config: APIPlanDefinitionConfigType;
  created: string;
  modified: string;
}>;

export type APIPlanDefinitionRequestType = Partial<{
  plan_code: string;
  name: string;
  description: string;
  config: APIPlanDefinitionConfigType;
}>;

export type APIPlanPublishValidationErrorType = Partial<{
  field: string;
  code: string;
  message: string;
}>;

export type APIPlanResolvedPermissionProfilePresetType =
  APIPlanPermissionProfilePresetType &
    Partial<{
      preset_version: number;
      _resolved: boolean;
      name: string;
      compatible_roles: string[];
      contexts: string[];
      capabilities: string[];
    }>;

export type APIPlanResolvedAccessPresetsType = Partial<{
  permission_profile_presets: APIPlanResolvedPermissionProfilePresetType[];
  approval_level_presets: APIPlanApprovalLevelPresetType[];
  workspace_scope_rules: APIPlanWorkspaceScopeRulesType;
  vendor_access_rules: APIPlanVendorAccessRulesType;
  default_assignments: Record<string, APIPlanDefaultAssignmentType>;
}>;

export type APIPlanVersionResponseType = Partial<{
  id: number;
  version: number;
  config: APIPlanDefinitionConfigType;
  published_at: string;
  published_by: number;
}>;

export interface IAPIPlanDefinition {
  getData(): APIPlanDefinitionResponseType;
  isDataValid(): boolean;
  toRequestModel(): APIPlanDefinitionRequestType;
}

export class APIPlanDefinition implements IAPIPlanDefinition {
  readonly data: APIPlanDefinitionResponseType;

  constructor(data: APIPlanDefinitionResponseType) {
    this.data = data;
  }

  getData(): APIPlanDefinitionResponseType {
    return this.data;
  }

  isDataValid(): boolean {
    return !!this.data.plan_code && !!this.data.name;
  }

  toRequestModel(): APIPlanDefinitionRequestType {
    if (this.isDataValid()) {
      return {
        plan_code: this.data.plan_code!.trim(),
        name: this.data.name!.trim(),
        description: this.data.description ?? "",
        config: this.data.config ?? {},
      };
    }
    throw new Error("Invalid data: plan_code and name are required");
  }
}
